import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { normalizePagePath, notFoundMetadata, pageMetadata, type PageMetadata as Metadata } from '../../../packages/shared/src/site-metadata';
import { pageStructuredData, serializeStructuredData } from '../../../packages/shared/src/site-structured-data';

export function updatePageMetadata(metadata: Metadata) {
  document.title = metadata.title;
  const meta = (selector: string, attribute: string, name: string, content: string) => {
    let element = document.head.querySelector<HTMLMetaElement>(selector);
    if (!element) { element = document.createElement('meta'); element.setAttribute(attribute, name); document.head.append(element); }
    element.content = content;
  };
  const origin = document.head.querySelector<HTMLMetaElement>('meta[name="relay-origin"]')?.content || location.origin;
  const path = normalizePagePath(location.pathname);
  const url = `${origin}${path}`;
  const indexable = metadata.index && document.head.querySelector<HTMLMetaElement>('meta[name="relay-indexing"]')?.content === 'enabled';
  meta('meta[name="description"]', 'name', 'description', metadata.description);
  meta('meta[name="robots"]', 'name', 'robots', indexable ? 'index, follow, max-image-preview:large' : 'noindex, nofollow');
  for (const [key, value] of Object.entries({ 'og:title': metadata.title, 'og:description': metadata.description, 'og:url': url, 'og:image': metadata.image || `${origin}/og-image.png`, 'og:image:secure_url': metadata.image || `${origin}/og-image.png`, 'og:image:type': metadata.image ? 'image/jpeg' : 'image/png', 'og:image:alt': metadata.imageAlt || 'Relay — The right card for every introduction.' })) meta(`meta[property="${key}"]`, 'property', key, value);
  for (const [key, value] of Object.entries({ 'twitter:card': 'summary_large_image', 'twitter:title': metadata.title, 'twitter:description': metadata.description, 'twitter:image': metadata.image || `${origin}/og-image.png`, 'twitter:image:alt': metadata.imageAlt || 'Relay digital business cards for every introduction.' })) meta(`meta[name="${key}"]`, 'name', key, value);
  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.append(canonical); }
  canonical.href = url;
  const structuredData = indexable ? pageStructuredData(path, origin) : null;
  let script = document.getElementById('relay-structured-data');
  if (structuredData) {
    if (!script) { script = document.createElement('script'); script.id = 'relay-structured-data'; script.setAttribute('type', 'application/ld+json'); document.head.append(script); }
    script.textContent = serializeStructuredData(structuredData);
  } else script?.remove();
}

export default function PageMetadata() {
  const { pathname } = useLocation();
  useEffect(() => {
    // The card loader supplies metadata from its public, visible fields.
    if (!/^\/c\/[^/]+\/?$/.test(pathname)) updatePageMetadata(pageMetadata(pathname) || notFoundMetadata);
    window.dispatchEvent(new Event('relay:pageview'));
  }, [pathname]);
  return null;
}
